import { CALENDAR_UNITS, INTERVAL_BASES } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { Controller, type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { type Reauth, ReauthField, useReauth } from '../../approval-policy.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import work from './PmsWork.module.css';
import { frequencyLabel } from './plan-view.js';
import type { CalendarUnit, IntervalChangeInput, JobView } from './types.js';

export interface IntervalValues {
  calendarEvery: number | '';
  calendarUnit: CalendarUnit;
  runningHours: number | '';
  intervalBasis: (typeof INTERVAL_BASES)[number];
  reason: string;
  classReference: string;
}

function intervalOf(
  values: Pick<IntervalValues, 'calendarEvery' | 'calendarUnit' | 'runningHours'>,
) {
  return {
    calendar:
      values.calendarEvery === ''
        ? null
        : { every: values.calendarEvery, unit: values.calendarUnit },
    runningHours: values.runningHours === '' ? null : values.runningHours,
  };
}

/**
 * Aralık değişikliği talebi (K11): yeni aralık öncekinden farklı ve en az bir tetikleyicili;
 * gerekçe zorunlu (parola formun dışında, `useReauth`; ADR 0075). Kısaltma hemen etkili, uzatma süperintendent onayına gider
 * (`maintenance_interval_change`); klas kaleminde 60 ay sınırını sunucu denetler.
 */
export function intervalSchema(job: Pick<JobView, 'interval'>) {
  return z
    .object({
      calendarEvery: z.union([z.number(), z.literal('')]),
      calendarUnit: z.enum(CALENDAR_UNITS),
      runningHours: z.union([z.number(), z.literal('')]),
      intervalBasis: z.enum(INTERVAL_BASES),
      reason: z.string(),
      classReference: z.string(),
    })
    .superRefine((v, ctx) => {
      const issue = (path: string, message: string) =>
        ctx.addIssue({ code: 'custom', message, path: [path] });
      const next = intervalOf(v);
      if (
        next.calendar === null &&
        next.runningHours === null &&
        job.interval.conditionPointIds.length === 0
      ) {
        issue('calendarEvery', 'INTERVAL_REQUIRED');
      }
      const before = {
        calendar: job.interval.calendar
          ? { every: job.interval.calendar.every, unit: job.interval.calendar.unit }
          : null,
        runningHours: job.interval.runningHours,
      };
      if (JSON.stringify(next) === JSON.stringify(before))
        issue('calendarEvery', 'INTERVAL_UNCHANGED');
      if (v.reason.trim() === '') issue('reason', 'REQUIRED');
    });
}

export function toIntervalChange(
  job: Pick<JobView, 'interval'>,
  values: IntervalValues,
): IntervalChangeInput {
  return {
    interval: { ...intervalOf(values), conditionPointIds: [...job.interval.conditionPointIds] },
    intervalBasis: values.intervalBasis,
    reason: values.reason.trim(),
    classReference: values.classReference.trim() === '' ? null : values.classReference.trim(),
  };
}

function initial(job: JobView | null): IntervalValues {
  return {
    calendarEvery: job?.interval.calendar?.every ?? '',
    calendarUnit: job?.interval.calendar?.unit ?? 'month',
    runningHours: job?.interval.runningHours ?? '',
    intervalBasis: (INTERVAL_BASES as readonly string[]).includes(job?.intervalBasis ?? '')
      ? (job?.intervalBasis as IntervalValues['intervalBasis'])
      : 'maker',
    reason: '',
    classReference: '',
  };
}

/**
 * "Request interval change" (gemide, kayıttan sonra; K11): güncel aralık görünür; yeni aralık,
 * dayanak, gerekçe, klas referansı ve açık onay adımı (parola yalnız şirket ayarı açıkken; ADR
 * 0075). Bekleyen uzatma "Interval change pending" rozetiyle
 * görünür.
 */
export function IntervalChangeDialog({
  opened,
  job,
  onClose,
  onSubmit,
  variant = 'ship',
}: {
  opened: boolean;
  job: JobView | null;
  onClose: () => void;
  onSubmit: (job: JobView, input: IntervalChangeInput) => Promise<void>;
  /**
   * `office`: etkin gemide ofisin aralık kararı (ADR 0104); gemi ilk senkronda onaysız uygular.
   * Pencere ve alanlar aynı; başlık, açıklama ve düğme farklı.
   */
  variant?: 'ship' | 'office';
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [error, setError] = useState<unknown>(null);
  const reauth = useReauth('maintenance_interval_change');
  const resetPassword = reauth.reset;
  const form = useForm<IntervalValues>({
    resolver: zodResolver(
      intervalSchema(
        job ?? { interval: { calendar: null, runningHours: null, conditionPointIds: [] } },
      ),
    ),
    defaultValues: initial(job),
  });
  useEffect(() => {
    if (opened) {
      form.reset(initial(job));
      resetPassword();
      setError(null);
    }
  }, [opened, job, form, resetPassword]);
  if (!job) return null;
  const submit = form.handleSubmit(async (values) => {
    if (reauth.missing) return;
    setError(null);
    try {
      await onSubmit(job, { ...toIntervalChange(job, values), ...reauth.body() });
    } catch (failure) {
      reauth.noteError(failure);
      setError(failure);
    }
  });
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(variant === 'office' ? 'pms.work.interval.officeTitle' : 'pms.work.interval.title', {
        code: job.code,
      })}
      closeButtonProps={{ 'aria-label': t('pms.work.password.back') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <IntervalFields form={form} job={job} reauth={reauth} variant={variant} />
        {error ? (
          <p className={work.error} role="alert">
            {errorText(error)}
          </p>
        ) : null}
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('pms.work.password.back')}
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting} disabled={reauth.missing}>
            {t(
              variant === 'office' ? 'pms.work.interval.officeSubmit' : 'pms.work.interval.submit',
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function IntervalFields({
  form,
  job,
  reauth,
  variant = 'ship',
}: {
  form: UseFormReturn<IntervalValues>;
  reauth: Reauth;
  job: JobView;
  variant?: 'ship' | 'office';
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string) => t(`pms.work.interval.${key}`);
  const error = (name: keyof IntervalValues) => {
    const message = form.formState.errors[name]?.message;
    return message
      ? t(`pms.fieldErrors.${message}`, {
          defaultValue: t(`pms.errors.${message}`, {
            defaultValue: t('pms.errors.VALIDATION_FAILED'),
          }),
        })
      : undefined;
  };
  return (
    <Stack gap="sm">
      <dl className={work.facts}>
        <div>
          <dt>{k('current')}</dt>
          <dd>{frequencyLabel(job.interval, t, i18n.resolvedLanguage ?? 'en')}</dd>
        </div>
      </dl>
      <p className={work.note}>{k(variant === 'office' ? 'officeHint' : 'hint')}</p>
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Controller
          control={form.control}
          name="calendarEvery"
          render={({ field }) => (
            <NumberInput
              label={t('pms.work.job.calendarEvery')}
              min={1}
              decimalScale={0}
              value={field.value}
              onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
              error={error('calendarEvery')}
            />
          )}
        />
        <Controller
          control={form.control}
          name="calendarUnit"
          render={({ field }) => (
            <Select
              label={t('pms.work.job.calendarUnit')}
              data={CALENDAR_UNITS.map((u) => ({ value: u, label: t(`pms.work.units.${u}`) }))}
              value={field.value}
              allowDeselect={false}
              onChange={(value) => field.onChange(value ?? 'month')}
            />
          )}
        />
        <Controller
          control={form.control}
          name="runningHours"
          render={({ field }) => (
            <NumberInput
              label={t('pms.work.job.runningHours')}
              min={0}
              decimalScale={1}
              value={field.value}
              onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
              error={error('runningHours')}
            />
          )}
        />
      </SimpleGrid>
      <Controller
        control={form.control}
        name="intervalBasis"
        render={({ field }) => (
          <Select
            label={t('pms.work.job.intervalBasis')}
            data={INTERVAL_BASES.map((b) => ({ value: b, label: t(`pms.work.bases.${b}`) }))}
            value={field.value}
            allowDeselect={false}
            onChange={(value) => field.onChange(value ?? 'maker')}
          />
        )}
      />
      <Textarea
        label={k('reason')}
        required
        autosize
        minRows={2}
        {...form.register('reason')}
        error={error('reason')}
      />
      <TextInput label={k('classReference')} {...form.register('classReference')} />
      <ReauthField
        reauth={reauth}
        label={t('pms.work.password.password')}
        description={t('pms.work.password.hint')}
        mb="0"
      />
    </Stack>
  );
}
